import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Ban,
  CalendarClock,
  FileText,
  MessageSquare,
  Pause,
  Send,
  UserMinus,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { useChannelQuotas } from '@/features/channel-quota/model/use-channel-quotas';
import { EntityListCard, type EntityListCardBadge } from '@/shared/components/entity-list-card';
import { PageHeader } from '@/shared/components/page-header';
import { EmptyState } from '@/shared/components/empty-state';
import { KpiCard } from '@/shared/components/kpi-card';
import { LeadAvatar } from '@/shared/components/lead-avatar';
import { cn } from '@/shared/lib/cn';
import { formatDate, formatTime, percent } from '@/shared/lib/format';
import { usePassbook } from '@/shared/lib/passbook';
import { Badge } from '@/shared/ui/badge';
import { Card } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Pagination } from '@/shared/ui/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Separator } from '@/shared/ui/separator';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  applyMerge,
  AudienceSummary,
  CAMPAIGN_STATUS_META,
  ChannelErrorState,
  ChannelLoading,
  ConsumptionPanel,
  formatCount,
  RECIPIENT_STATUS_META,
  type CampaignStatus,
  type RecipientStatus,
} from '@/features/marketing-shared';
import { BackToSmsCampaigns, SmsCampaignActions } from './campaign-actions';
import { formatSmsSender } from '../model/preview';
import { finalizeSmsText, measureSmsTextFor, smsCountingLabel, textRulesFor } from '../model/text';
import { useSmsMarketing } from '../model/context';
import { SMS_RECIPIENTS_PAGE_SIZE, useSmsCampaignDetail } from '../model/use-sms-campaign-detail';
import type { SmsCampaign, SmsMetrics } from '../model/types';
import { SmsPhonePreview, smsPreviewLayoutClass } from './sms-preview';

const RECIPIENT_FILTERS: Array<{ value: 'all' | RecipientStatus; label: string }> = [
  { value: 'all', label: 'Todos' },
  ...(Object.keys(RECIPIENT_STATUS_META) as RecipientStatus[]).map((status) => ({
    value: status,
    label: RECIPIENT_STATUS_META[status].label,
  })),
];

const PENDING_RESULTS: Record<
  CampaignStatus,
  { icon: LucideIcon; title: string; description: string }
> = {
  draft: {
    icon: FileText,
    title: 'Rascunho sem resultados',
    description: 'Os números aparecem quando o envio começar.',
  },
  scheduled: {
    icon: CalendarClock,
    title: 'Agendada, ainda sem envio',
    description: 'Os números entram no horário marcado.',
  },
  sending: {
    icon: Send,
    title: 'Envio em andamento',
    description: 'Ainda não há números registrados.',
  },
  sent: {
    icon: MessageSquare,
    title: 'Envio concluído',
    description: 'Nenhum total foi registrado nesta campanha.',
  },
  paused: {
    icon: Pause,
    title: 'Envio pausado',
    description: 'Ainda não há resultados de envio.',
  },
  failed: {
    icon: Ban,
    title: 'Envio com falha',
    description: 'Nenhum total foi registrado.',
  },
  cancelled: {
    icon: XCircle,
    title: 'Envio cancelado',
    description: 'A campanha foi cancelada antes de gerar resultados.',
  },
};

function whenLabel(campaign: SmsCampaign): string {
  if (!campaign.scheduledAt) return 'Sem data de envio';
  if (campaign.sendMode === 'now') return `Na aprovação · ${formatDate(campaign.scheduledAt)} ${formatTime(campaign.scheduledAt)}`;
  return `${formatDate(campaign.scheduledAt)} ${formatTime(campaign.scheduledAt)}`;
}

function partsLabel(parts: number): string {
  if (parts <= 1) return '1 SMS';
  return `${formatCount(parts)} SMS`;
}

function metricsLead(status: CampaignStatus, metrics: SmsMetrics): string {
  const delivered = `${formatCount(metrics.delivered)} entregues de ${formatCount(metrics.sent)} enviados`;
  switch (status) {
    case 'sending':
      return `Em andamento · ${delivered}`;
    case 'sent':
      return `Concluída · ${delivered}`;
    case 'failed':
      return `Com falha · ${delivered}`;
    case 'paused':
      return `Pausada · ${delivered}`;
    case 'scheduled':
      return `Agendada · ${delivered}`;
    case 'draft':
      return `Rascunho · ${delivered}`;
    case 'cancelled':
      return `Cancelada · ${delivered}`;
    default: {
      const unreachable: never = status;
      return unreachable;
    }
  }
}

function rateHint(part: number, total: number): string | undefined {
  if (total <= 0) return undefined;
  return `${percent(part, total)} dos enviados`;
}

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 break-words text-sm font-medium leading-snug">{value}</dd>
    </div>
  );
}

export function SmsCampaignDetailPage() {
  const { campaignId } = useParams();
  const { idPassbook, passbook } = usePassbook();
  const { quotas } = useChannelQuotas(idPassbook);
  const [page, setPage] = useState(1);
  const { campaign, state, detailPending } = useSmsCampaignDetail(campaignId, page);
  const { numbers } = useSmsMarketing();
  const [recipientStatus, setRecipientStatus] = useState<'all' | RecipientStatus>('all');
  const [query, setQuery] = useState('');
  const storeName = passbook.organizationName || 'Sua loja';

  useEffect(() => {
    setPage(1);
    setRecipientStatus('all');
    setQuery('');
  }, [campaignId]);

  const recipientsPage = campaign?.recipients;
  const recipients = useMemo(() => {
    const rows = recipientsPage?.rows ?? [];
    const term = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (recipientStatus !== 'all' && row.status !== recipientStatus) return false;
      if (!term) return true;
      return row.name.toLowerCase().includes(term) || row.reason.toLowerCase().includes(term);
    });
  }, [recipientsPage, query, recipientStatus]);

  if (!campaign && state.phase === 'loading') {
    return (
      <>
        <PageHeader title="Campanha de SMS" leading={<BackToSmsCampaigns />} />
        <ChannelLoading label="Carregando a campanha" />
      </>
    );
  }

  if (!campaign && state.phase === 'error' && state.error?.kind !== 'not_found') {
    return (
      <>
        <PageHeader title="Campanha de SMS" leading={<BackToSmsCampaigns />} />
        <ChannelErrorState title="Não foi possível carregar a campanha" error={state.error} onRetry={state.refetch} />
      </>
    );
  }

  if (!campaign) {
    return (
      <>
        <PageHeader title="Campanha não encontrada" leading={<BackToSmsCampaigns />} />
        <Card>
          <EmptyState
            icon={MessageSquare}
            title="Campanha não encontrada"
            description="Esse identificador não existe nesta loja ou a campanha foi excluída."
          />
        </Card>
      </>
    );
  }

  const meta = CAMPAIGN_STATUS_META[campaign.status];
  const metrics = campaign.metrics;
  // Só o rascunho confere o saldo: agendada e pausada já reservaram na
  // aprovação, e o saldo que sobrou já desconta a própria campanha.
  const showEstimate = campaign.status === 'draft';
  // Mesma regra do remetente que a revisão usou (instrução de saída e
  // contagem do provedor); remetente que não está mais na conta, por segmento.
  const rules = textRulesFor(numbers, campaign.from);
  const preview = finalizeSmsText(applyMerge(campaign.text, storeName), rules);
  const measure = measureSmsTextFor(preview, rules);
  const pending = PENDING_RESULTS[campaign.status];
  const createdLabel = campaign.createdAt ? `${formatDate(campaign.createdAt)} ${formatTime(campaign.createdAt)}`.trim() : '';
  const detailFailed = state.phase === 'error' && state.error?.kind !== 'not_found';

  return (
    <div className="min-w-0">
      <div className="space-y-5">
        <PageHeader
          className="mb-0"
          title={campaign.name}
          description={whenLabel(campaign)}
          leading={<BackToSmsCampaigns />}
          actions={
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Badge variant={meta.variant}>{meta.label}</Badge>
              <SmsCampaignActions campaign={campaign} />
            </div>
          }
        />

        <AudienceSummary audience={campaign.audience} headingId="sms-publico" tone="kpi" />

        {campaign.failureSummary ? (
          <p
            className="rounded-xl border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm"
            role="status"
          >
            {campaign.failureSummary}
          </p>
        ) : null}

        {detailFailed ? (
          <ChannelErrorState
            title="Resultados e destinatários não carregaram"
            error={state.error}
            onRetry={state.refetch}
          />
        ) : null}

        <section aria-labelledby="sms-resultados" className="space-y-3">
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <h2 id="sms-resultados" className="text-sm font-medium">
              Resultados
            </h2>
            {metrics ? (
              <p className="text-xs text-muted-foreground">
                {metricsLead(campaign.status, metrics)}
              </p>
            ) : null}
          </div>
          {metrics ? (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <KpiCard label="Enviados" value={formatCount(metrics.sent)} icon={Send} index={0} />
              <KpiCard
                label="Entregues"
                value={formatCount(metrics.delivered)}
                icon={MessageSquare}
                hint={rateHint(metrics.delivered, metrics.sent)}
                index={1}
              />
              <KpiCard
                label="Rejeitados"
                value={formatCount(metrics.rejected)}
                icon={Ban}
                hint={rateHint(metrics.rejected, metrics.sent)}
                index={2}
              />
              <KpiCard
                label="Descadastro"
                value={formatCount(metrics.unsubscribed)}
                icon={UserMinus}
                hint={
                  metrics.unsubscribed > 0
                    ? rateHint(metrics.unsubscribed, metrics.sent)
                    : undefined
                }
                index={3}
              />
            </div>
          ) : detailPending && !detailFailed ? (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" aria-busy="true">
              {[0, 1, 2, 3].map((index) => (
                <Skeleton key={index} className="h-24 rounded-2xl" />
              ))}
            </div>
          ) : (
            <Card>
              <EmptyState
                compact
                icon={pending.icon}
                title={pending.title}
                description={pending.description}
              />
            </Card>
          )}
        </section>

        {showEstimate && quotas ? (
          <ConsumptionPanel
            eligible={campaign.audience.eligible}
            capacity={quotas.sms}
            channel="sms"
            segments={Math.max(measure.parts, 1)}
          />
        ) : null}

        <div className={cn(smsPreviewLayoutClass, 'lg:items-stretch')}>
          <Card className="flex h-full min-w-0 flex-col p-4 sm:p-5">
            <h2 className="text-sm font-medium">Resumo</h2>
            <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3.5">
              <MetaItem label="Remetente" value={formatSmsSender(campaign.from)} />
              <MetaItem label="Codificação" value={smsCountingLabel(measure, rules)} />
              <MetaItem label="Segmentos" value={partsLabel(measure.parts)} />
              <MetaItem
                label="Público"
                value={`${campaign.audience.audienceName} · ${formatCount(campaign.audience.eligible)}`}
              />
              <MetaItem label="Envio" value={whenLabel(campaign)} />
              <MetaItem label="Criada" value={createdLabel || '—'} />
            </dl>
            <Separator className="my-4 bg-border/70" />
            <div className="flex min-h-[9rem] flex-1 flex-col justify-center rounded-xl bg-foreground/[0.04] px-4 py-5 sm:px-5">
              <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Mensagem
              </p>
              <p className="mt-2 whitespace-pre-wrap break-words text-base leading-relaxed">
                {preview.trim() ? preview : 'Sem texto nesta campanha.'}
              </p>
            </div>
          </Card>

          <Card className="flex h-full min-w-0 flex-col p-4 sm:p-5">
            <SmsPhonePreview
              framed
              className="min-h-0 flex-1"
              message={preview}
              sender={campaign.from}
            />
          </Card>
        </div>

        {recipientsPage && recipientsPage.total > 0 ? (
          <section className="space-y-3" aria-labelledby="sms-destinatarios" aria-busy={detailPending ? 'true' : undefined}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 id="sms-destinatarios" className="text-sm font-medium">
                Destinatários
              </h2>
              <p className="text-xs tabular-nums text-muted-foreground">
                {formatCount(recipientsPage.total)} no total
              </p>
            </div>
            <div className="grid gap-2 sm:flex sm:flex-wrap">
              <div className="min-w-0">
                <Label htmlFor="estado-sms-dest" className="sr-only">
                  Estado
                </Label>
                <Select
                  value={recipientStatus}
                  onValueChange={(value) => setRecipientStatus(value as 'all' | RecipientStatus)}
                >
                  <SelectTrigger id="estado-sms-dest" className="w-full sm:w-44">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RECIPIENT_FILTERS.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-0 sm:w-64">
                <Label htmlFor="busca-sms-dest" className="sr-only">
                  Buscar
                </Label>
                <Input
                  id="busca-sms-dest"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Buscar por nome"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              O filtro vale para a página carregada ({formatCount(recipientsPage.rows.length)} de {formatCount(recipientsPage.total)}).
            </p>
            <ul className="space-y-2.5">
              {recipients.length === 0 ? (
                <li className="text-sm text-muted-foreground">
                  Nenhum destinatário com esse filtro.
                </li>
              ) : (
                recipients.map((row, index) => {
                  const rowMeta = RECIPIENT_STATUS_META[row.status];
                  const badges: EntityListCardBadge[] = [
                    {
                      key: 'status',
                      label: rowMeta.label,
                      variant: rowMeta.variant,
                      wide: true,
                      copyLines: [
                        { label: 'Estado', value: rowMeta.label },
                        { label: 'Telefone', value: row.phoneMasked },
                        ...(row.reason ? [{ label: 'Motivo', value: row.reason }] : []),
                      ],
                    },
                  ];
                  return (
                    <li key={row.id}>
                      <EntityListCard
                        selected={false}
                        onToggle={() => undefined}
                        selectable={false}
                        index={index}
                        ariaLabel={row.name}
                        title={row.name}
                        subtitle={
                          <p className="text-xs text-muted-foreground sm:text-sm [overflow-wrap:anywhere]">
                            {row.phoneMasked}
                            {row.reason ? ` · ${row.reason}` : ''}
                          </p>
                        }
                        thumbnail={
                          <LeadAvatar
                            lead={{ _id: row.id, profile: { name: row.name } }}
                            className="size-11"
                          />
                        }
                        badges={badges}
                        actions={null}
                      />
                    </li>
                  );
                })
              )}
            </ul>
            {recipientsPage.total > recipientsPage.pageSize ? (
              <Pagination
                page={recipientsPage.page - 1}
                pageSize={recipientsPage.pageSize || SMS_RECIPIENTS_PAGE_SIZE}
                total={recipientsPage.total}
                onPageChange={(next) => setPage(next + 1)}
                className="justify-center pt-1"
              />
            ) : null}
          </section>
        ) : null}
      </div>
    </div>
  );
}
